import { useQuery } from '@tanstack/react-query';
import { matchPath, useLocation } from 'react-router';
import { useApi } from '../api/context';
import { queryKeys } from '../api/queries';
import type { Blueprint, Deployment, Network, Run } from '../api/types';
import { formatClock } from '../lib/format';
import { strings } from '../strings';

/** Which page the route shows, as far as the shell needs it (breadcrumb, palette). */
export type RouteContext =
  | { page: 'blueprints' }
  | { page: 'designer' | 'versions' | 'deployments'; blueprintId: string }
  | { page: 'deployment'; deploymentId: string }
  | { page: 'run'; runId: string }
  | { page: 'all-deployments' }
  | { page: 'runs' }
  | { page: 'cloud-accounts' }
  | { page: 'cloud-account'; cloudAccountId: string }
  | { page: 'networks' }
  | { page: 'network'; networkId: string }
  | { page: 'about' }
  | { page: 'whats-new' }
  | { page: 'other' };

const PATTERNS: [string, (params: Record<string, string | undefined>) => RouteContext][] = [
  ['/blueprints', () => ({ page: 'blueprints' })],
  ['/blueprints/:id', (p) => ({ page: 'designer', blueprintId: p.id! })],
  ['/blueprints/:id/versions', (p) => ({ page: 'versions', blueprintId: p.id! })],
  ['/blueprints/:id/deployments', (p) => ({ page: 'deployments', blueprintId: p.id! })],
  ['/deployments', () => ({ page: 'all-deployments' })],
  ['/deployments/:id', (p) => ({ page: 'deployment', deploymentId: p.id! })],
  ['/runs', () => ({ page: 'runs' })],
  ['/runs/:id', (p) => ({ page: 'run', runId: p.id! })],
  ['/cloud-accounts', () => ({ page: 'cloud-accounts' })],
  ['/cloud-accounts/:id', (p) => ({ page: 'cloud-account', cloudAccountId: p.id! })],
  ['/networks', () => ({ page: 'networks' })],
  ['/networks/:id', (p) => ({ page: 'network', networkId: p.id! })],
  ['/about', () => ({ page: 'about' })],
  ['/whats-new', () => ({ page: 'whats-new' })],
];

export function routeContext(pathname: string): RouteContext {
  for (const [pattern, build] of PATTERNS) {
    const match = matchPath({ path: pattern, end: true }, pathname);
    if (match) return build(match.params);
  }
  return { page: 'other' };
}

/** The rail destination a path belongs to (the route starts with its path). */
export function railSection(
  pathname: string,
): 'blueprints' | 'deployments' | 'networks' | 'runs' | 'cloud-accounts' | 'help' | null {
  if (pathname === '/blueprints' || pathname.startsWith('/blueprints/')) return 'blueprints';
  if (pathname === '/deployments' || pathname.startsWith('/deployments/')) return 'deployments';
  if (pathname === '/networks' || pathname.startsWith('/networks/')) return 'networks';
  if (pathname === '/runs' || pathname.startsWith('/runs/')) return 'runs';
  if (pathname === '/cloud-accounts' || pathname.startsWith('/cloud-accounts/')) return 'cloud-accounts';
  if (pathname === '/about' || pathname === '/whats-new') return 'help';
  return null;
}

/** "Plan · 10:42": a run's kind and start time, as the breadcrumb shows it. */
export function runLabel(run: Pick<Run, 'kind' | 'createdAt'>): string {
  return strings.shell.runLabel(strings.status.runKind[run.kind], formatClock(run.createdAt));
}

export interface RouteEntities {
  context: RouteContext;
  blueprintId: string | null;
  deploymentId: string | null;
  blueprint: Blueprint | undefined;
  deployment: Deployment | undefined;
  run: Run | undefined;
  /** Feature spec 17: the network of a network page or of a network run. */
  networkId: string | null;
  network: Network | undefined;
}

/**
 * The blueprint, deployment and run of the current route, from the same queries (keys and shapes) the
 * pages use, so the shell adds no requests on pages that load them anyway.
 */
export function useRouteEntities(): RouteEntities {
  const api = useApi();
  const context = routeContext(useLocation().pathname);

  const runId = context.page === 'run' ? context.runId : null;
  const runQuery = useQuery({
    queryKey: queryKeys.run(runId ?? ''),
    queryFn: () => api.getRun(runId!),
    enabled: runId !== null,
  });
  const run = runId ? runQuery.data : undefined;

  // Network runs have no deployment (their `deploymentId` is null).
  const deploymentId = context.page === 'deployment' ? context.deploymentId : run?.deploymentId || null;
  const deploymentQuery = useQuery({
    queryKey: queryKeys.deployment(deploymentId ?? ''),
    queryFn: () => api.getDeployment(deploymentId!),
    enabled: deploymentId !== null,
  });
  const deployment = deploymentId ? deploymentQuery.data?.data : undefined;

  const blueprintId =
    context.page === 'designer' || context.page === 'versions' || context.page === 'deployments'
      ? context.blueprintId
      : (deployment?.blueprintId ?? null);
  const blueprintQuery = useQuery({
    queryKey: queryKeys.blueprint(blueprintId ?? ''),
    queryFn: () => api.getBlueprint(blueprintId!),
    enabled: blueprintId !== null,
  });
  const blueprint = blueprintId ? blueprintQuery.data?.data : undefined;

  const networkId = context.page === 'network' ? context.networkId : run?.networkId || null;
  const networkQuery = useQuery({
    queryKey: queryKeys.network(networkId ?? ''),
    queryFn: () => api.getNetwork(networkId!),
    enabled: networkId !== null,
  });
  const network = networkId ? networkQuery.data?.data : undefined;

  return { context, blueprintId, deploymentId, blueprint, deployment, run, networkId, network };
}
